import App from "../app";

export default class Drawer {
    constructor(header) {
        this.header = header;

        this.drawer = document.getElementById("drawer");
        this.toggleBtn = this.header.getToggleButton();
        this.defaultLogoColor = "black";

        this.navItems = this.drawer.querySelectorAll(".no-drawer__nav-item");
        this.navLinks = this.drawer.querySelectorAll(".no-drawer__nav-link");
        this.subTriggers = this.drawer.querySelectorAll(
            ".has-sub > .no-drawer__nav-link"
        );

        this.isDrawerOpen = false;

        this.timeline = gsap.timeline({ paused: true });

        this.initSplitText();

        this.buildAnimation();
        this.bindEvents();
        this.bindTextHover();
        this.activeScroll();

        // 메인 페이지인지 확인
        this.isMainPage =
            this.header.header.classList.contains("no-header--main");

        if (this.isMainPage) {
            this.defaultLogoColor = "white";
        } else {
            this.defaultLogoColor = "black";
        }

        // 초기 스크롤 상태에 따라 로고 색상 설정
        this.updateInitialLogo();
    }

    activeScroll() {
        // 초기 스크롤 상태 체크
        this.updateInitialLogo();

        window.addEventListener("scroll", () => {
            if (this.isDrawerOpen) return;

            if (this.header.isScrolling()) {
                this.updateLogo("white");
            } else {
                this.updateLogo(this.defaultLogoColor);
            }
        });
    }

    updateInitialLogo() {
        if (this.isDrawerOpen) return;

        // 스크롤 상태에 따라 로고 색상 설정
        if (this.header.isScrolling()) {
            // 스크롤 중: 항상 white
            this.updateLogo("white");
        } else {
            // 스크롤 안 함: 메인은 white, 서브는 black
            this.updateLogo(this.defaultLogoColor);
        }
    }

    initSplitText() {
        this.navLinks.forEach((link) => {
            const text = link.textContent.trim();
            link.setAttribute("data-text", text);

            // SplitType으로 글자 분리
            const split = new SplitType(link, {
                types: "chars",
                tagName: "span",
            });

            // 원본 chars 가져오기
            const originalChars = Array.from(link.querySelectorAll(".char"));

            // 링크 내용 비우기
            link.innerHTML = "";

            // top과 bottom div 생성
            const topDiv = document.createElement("div");
            topDiv.className = "no-drawer__nav-text-top";

            const bottomDiv = document.createElement("div");
            bottomDiv.className = "no-drawer__nav-text-bottom";

            // 각 char를 top과 bottom에 복사
            originalChars.forEach((char) => {
                const topChar = char.cloneNode(true);
                const bottomChar = char.cloneNode(true);

                topDiv.appendChild(topChar);
                bottomDiv.appendChild(bottomChar);
            });

            link.appendChild(topDiv);
            link.appendChild(bottomDiv);

            // GSAP 타임라인 생성
            const topChars = topDiv.querySelectorAll(".char");
            const bottomChars = bottomDiv.querySelectorAll(".char");

            const tl = gsap.timeline({ paused: true });

            tl.to(
                topChars,
                {
                    yPercent: -100,
                    duration: 0.5,
                    stagger: 0.015,
                    ease: "power3.inOut",
                },
                0
            ).to(
                bottomChars,
                {
                    yPercent: -100,
                    duration: 0.5,
                    stagger: 0.015,
                    ease: "power3.inOut",
                },
                0
            );

            link.hoverTimeline = tl;
        });
    }

    bindTextHover() {
        this.navLinks.forEach((link) => {
            link.addEventListener("mouseenter", () => {
                if (link.hoverTimeline) {
                    link.hoverTimeline.play();
                }
            });

            link.addEventListener("mouseleave", () => {
                if (link.hoverTimeline) {
                    link.hoverTimeline.reverse();
                }
            });
        });
    }

    buildAnimation() {
        gsap.set(this.drawer, { y: "-100%", opacity: 0 });

        const search = this.drawer.querySelector(".no-drawer__search");
        const info = this.drawer.querySelector(".no-drawer__info");
        const langBadges = this.drawer.querySelector(".no-drawer__lang-badges");
        const privacy = this.drawer.querySelector(".no-drawer__privacy");
        const brandFilm = this.drawer.querySelector(".no-drawer__brand-film");
        const footerSns = this.drawer.querySelector(".no-drawer__footer-sns");
        const footer = this.drawer.querySelector(".no-drawer__footer");
        const backgroundText = this.drawer.querySelector(
            ".no-drawer__background-text"
        );

        this.timeline
            // Drawer 슬라이드 다운
            .to(this.drawer, {
                y: "0%",
                opacity: 1,
                duration: 0.6,
                ease: "power3.out",
            })
            // 메뉴 항목 순차 등장 (빠르게)
            .from(
                this.navItems,
                {
                    y: -20,
                    opacity: 0,
                    stagger: 0.04,
                    duration: 0.4,
                    ease: "power2.out",
                },
                "-=0.3"
            )
            // 우측 요소들 그룹 애니메이션
            .from(
                [search, info, privacy].filter((el) => el),
                {
                    x: 15,
                    opacity: 0,
                    stagger: 0.06,
                    duration: 0.35,
                    ease: "power2.out",
                },
                "-=0.3"
            )
            // Footer 및 하단 요소들
            .from(
                [footer].filter((el) => el),
                {
                    y: 12,
                    opacity: 0,
                    duration: 0.3,
                    ease: "power2.out",
                },
                "-=0.2"
            )
            // 브랜드 필름 스케일 업
            .from(
                brandFilm,
                {
                    scale: 0.96,
                    opacity: 0,
                    duration: 0.45,
                    ease: "power2.out",
                },
                "-=0.25"
            )
            // 배경 텍스트
            .from(
                backgroundText,
                {
                    opacity: 0,
                    duration: 0.6,
                    ease: "power1.out",
                },
                "-=0.4"
            );

        this.timeline.pause();
    }

    bindEvents() {
        if (this.toggleBtn) {
            this.toggleBtn.addEventListener("click", () => {
                if (this.drawer.classList.contains("is-open")) {
                    this.close();
                } else {
                    this.open();
                }
            });
        }

        document.addEventListener("keydown", (e) => {
            if (
                e.key === "Escape" &&
                this.drawer.classList.contains("is-open")
            ) {
                this.close();
            }
        });

        // 검색 입력 필드 삭제 버튼
        this.bindSearchClear();

        // 서브메뉴 토글
        this.navLinks.forEach((link) => {
            const parent = link.closest(".has-sub");

            if (parent) {
                // 서브메뉴가 있는 경우
                const subList = parent.querySelector(".no-drawer__sub-list");

                if (subList) {
                    // 초기 상태 설정
                    gsap.set(subList, {
                        height: 0,
                        opacity: 0,
                        overflow: "hidden",
                    });

                    link.addEventListener("click", (e) => {
                        e.preventDefault();

                        const isOpen = parent.classList.contains("open");

                        if (isOpen) {
                            // 닫기
                            gsap.to(subList, {
                                height: 0,
                                opacity: 0,
                                duration: 0.3,
                                ease: "power2.inOut",
                                onComplete: () => {
                                    parent.classList.remove("open");
                                },
                            });
                        } else {
                            // 열기
                            parent.classList.add("open");

                            // 자동 높이 계산
                            gsap.set(subList, { height: "auto", opacity: 0 });
                            const autoHeight = subList.offsetHeight;
                            gsap.set(subList, { height: 0 });

                            gsap.to(subList, {
                                height: autoHeight,
                                opacity: 1,
                                duration: 0.4,
                                ease: "power2.out",
                            });

                            // 서브 링크 애니메이션
                            const subLinks = subList.querySelectorAll("li");
                            gsap.fromTo(
                                subLinks,
                                { opacity: 0, y: -10 },
                                {
                                    opacity: 1,
                                    y: 0,
                                    duration: 0.3,
                                    stagger: 0.04,
                                    ease: "power2.out",
                                    delay: 0.1,
                                }
                            );
                        }
                    });
                }
            }
            // 서브메뉴가 없으면 링크 이동 허용 (preventDefault 안함)
        });
    }

    bindSearchClear() {
        const searchInput = this.drawer.querySelector(".no-drawer__search-input");
        const clearBtn = this.drawer.querySelector(".no-drawer__search-clear");

        if (!searchInput || !clearBtn) return;

        // 입력값 변경 시 삭제 버튼 표시/숨김
        const toggleClearButton = () => {
            if (searchInput.value.trim().length > 0) {
                clearBtn.style.display = "flex";
            } else {
                clearBtn.style.display = "none";
            }
        };

        // 초기 상태 확인
        toggleClearButton();

        // 입력 이벤트
        searchInput.addEventListener("input", toggleClearButton);
        searchInput.addEventListener("focus", toggleClearButton);
        searchInput.addEventListener("blur", () => {
            // blur 시에도 값이 있으면 버튼 유지
            setTimeout(toggleClearButton, 100);
        });

        // 삭제 버튼 클릭
        clearBtn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();
            
            searchInput.value = "";
            searchInput.focus();
            clearBtn.style.display = "none";
        });
    }

    open() {
        this.isDrawerOpen = true;
        this.drawer.classList.add("is-open");
        this.drawer.setAttribute("aria-hidden", "false");
        App.getInstance().setLock(true);

        // 햄버거 버튼을 X로 변경
        if (this.toggleBtn) {
            this.toggleBtn.classList.add("is-active");
        }

        // 헤더 상태 변경
        if (this.header && this.header.header) {
            this.header.header.classList.add("--drawer-open");
        }

        // 로고 이미지 변경 (흰색 -> 검정)
        this.updateLogo("black");

        this.timeline.progress(0).play();
    }

    close() {
        this.timeline.reverse().then(() => {
            this.drawer.classList.remove("is-open");
            this.drawer.setAttribute("aria-hidden", "true");
            App.getInstance().setLock(false);

            // X를 햄버거 버튼으로 변경
            if (this.toggleBtn) {
                this.toggleBtn.classList.remove("is-active");
            }

            // 헤더 상태 복원
            if (this.header && this.header.header) {
                this.header.header.classList.remove("--drawer-open");
            }

            // 로고 이미지 복원 (검정 -> 흰색)
            this.updateLogo(
                this.header.isScrolling() ? "white" : this.defaultLogoColor
            );

            this.isDrawerOpen = false;
        });
    }

    updateLogo(color) {
        const logo = document.querySelector(".no-header__logo img");

        if (!logo) return;

        const whiteSrc = logo.getAttribute("data-logo-white");
        const blackSrc = logo.getAttribute("data-logo-black");

        if (color === "black" && blackSrc) {
            logo.src = blackSrc;
        } else if (color === "white" && whiteSrc) {
            logo.src = whiteSrc;
        }
    }
}
